import { QuestionCircleOutlined, UnorderedListOutlined, UserOutlined } from '@ant-design/icons';
import { DTIProps } from '@xometry/ui';
import { ReactComponent as IconOutlinedLogout } from 'assets/icons/outlined-logout.min.svg';
import { ReactComponent as IconCheck } from 'assets/icons/solid-check.min.svg';
import { ReactComponent as UserIcon } from 'assets/images/person.min.svg';
import { amplitudeLogger, FAQ_CLICK } from 'common/amplitude';
import { LocaleEnum } from 'common/config/locales';
import { ENABLE_FAST_PAY_PAGE, ENABLE_FINANCE_PAGE } from 'common/config/regions/features';
import { getRegionSupportedLocales } from 'common/config/regions/utils';
import { regionLinks } from 'common/constants/links';
import { DeviceTypes } from 'common/enums/deviceTypes';
import { useDeviceTypeByWindowSize } from 'common/hooks/useDeviceType';
import routes from 'common/routes';
import { AntButton } from 'components/ant/AntButton/AntButton';
import { AntMenu, AntMenuProps } from 'components/ant/AntMenu';
import { HeaderMenuSeparator } from 'components/Header/HeaderMenuSeparator/HeaderMenuSeparator';
import { langIcons } from 'components/LanguageChanger/LanguageChanger.const';
import { MessengerHeaderButton } from 'components/Messenger/MessengerHeaderButton/MessengerHeaderButton';
import { ContactType } from 'graphql/queries/types';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import styles from './HeaderMenu.module.less';

interface Props extends DTIProps {
  contact: ContactType;
  onLogout: () => void;
  onLocaleChange: (newLocale: LocaleEnum) => Promise<void>;
}

const ProfileSubmenuKey = 'profile-submenu';
const CheckedIcon = <IconCheck className={styles.checkedIcon} />;

const routeKeys = [
  routes.profileOnboarding,
  routes.offers,
  routes.orders,
  routes.profileDetails,
  routes.support,
  routes.finance,
  routes.calendar,
];
export const getSelectedKey = (pathName: string) =>
  routeKeys.reduce((acc: string[], item) => {
    if (pathName.includes(item)) {
      acc.push(item);
    }

    return acc;
  }, []);

export const HeaderMenu: FC<Props> = (props) => {
  const { 'data-testid': DTI, contact, onLogout, onLocaleChange } = props;
  const { t } = useTranslation();
  const deviceType = useDeviceTypeByWindowSize();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const languagesList = getRegionSupportedLocales();
  const location = useLocation();
  const selectedKeys = useMemo(() => getSelectedKey(location.pathname), [location.pathname]);

  const ProfileMenu = useMemo(() => {
    const list: AntMenuProps['items'] = [
      {
        key: routes.profileDetails,
        icon: <UserOutlined />,
        label: (
          <Link data-testid={`${DTI}__Item`} data-testvalue="profile" to={routes.profileDetails}>
            {t('header.menuItems.profile')}
          </Link>
        ),
      },
      {
        key: routes.support,
        icon: <UnorderedListOutlined />,
        label: (
          <Link data-testid={`${DTI}__Item`} data-testvalue="support" to={routes.support}>
            {t('header.menuItems.support')}
          </Link>
        ),
      },
      {
        key: 'faq',
        icon: <QuestionCircleOutlined />,
        label: (
          <a
            data-testid={`${DTI}__Item`}
            data-testvalue="faq"
            href={regionLinks.knowledgeBase}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => amplitudeLogger(FAQ_CLICK)}
          >
            {t('header.menuItems.faqAndHelp')}
          </a>
        ),
      },
      {
        key: 'locales',
        icon: langIcons[currentLocale],
        label: (
          <span data-testid={`${DTI}__CurrentLocale`} data-testvalue={currentLocale}>
            {currentLocale.toUpperCase()}
          </span>
        ),
        popupClassName: styles.popup,
        children: languagesList.map((l) => {
          const key = `locale_${l}`;
          const selected = l === currentLocale;
          const icon = langIcons[l];
          const label = (
            <span data-testid={`${DTI}__Locale`} data-testvalue={l}>
              {l.toUpperCase()}
            </span>
          );
          const itemIcon = selected ? CheckedIcon : undefined;
          const onClick = () => onLocaleChange(l);

          return {
            key,
            icon,
            label,
            itemIcon,
            onClick,
          };
        }),
      },
      {
        type: 'divider',
      },
    ];

    if (isDesktop) {
      list?.push({
        key: 'logout',
        icon: <IconOutlinedLogout />,
        label: (
          <AntButton
            data-testid={`${DTI}__LogOut`}
            type="link"
            onClick={() => void onLogout()}
            className={styles.logoutBtn}
            noPadding
          >
            {t('header.menuItems.logOut')}
          </AntButton>
        ),
      });
    }

    return list;
  }, [DTI, currentLocale, isDesktop, languagesList, onLocaleChange, onLogout, t]);

  const MenuItems = useMemo(() => {
    const list: AntMenuProps['items'] = [];

    if (!contact?.provider?.onboardingCompleted) {
      list.push({
        key: routes.profileOnboarding,
        label: (
          <Link data-testid={`${DTI}__Item`} data-testvalue="firstSteps" to={routes.profileOnboarding}>
            {t('header.menuItems.firstSteps')}
          </Link>
        ),
      });
    }

    list.push({
      key: routes.offers,
      label: (
        <Link data-testid={`${DTI}__Item`} data-testvalue="jobBoard" to={routes.offers}>
          {t('header.menuItems.jobBoard')}
        </Link>
      ),
    });

    list.push({
      key: routes.orders,
      label: (
        <Link data-testid={`${DTI}__Item`} data-testvalue="myOrders" to={routes.orders}>
          {t('header.menuItems.myOrders')}
        </Link>
      ),
    });

    if (ENABLE_FINANCE_PAGE) {
      list.push({
        key: routes.finance,
        label: (
          <Link data-testid={`${DTI}__Item`} data-testvalue="finance" to={routes.balance}>
            {t('header.menuItems.finance')}
          </Link>
        ),
      });
    }

    if (ENABLE_FAST_PAY_PAGE) {
      list.push({
        key: 'finance',
        label: (
          <span data-testid={`${DTI}__Item`} data-testvalue="finance">
            {t('header.menuItems.finance')}
          </span>
        ),
        children: [
          {
            key: routes.fastPay,
            label: (
              <Link data-testid={`${DTI}__Item`} data-testvalue="fastPay" to={routes.fastPay}>
                {t('fastPay.fastPay')}
              </Link>
            ),
          },
        ],
      });
    }

    list.push({
      key: routes.calendar,
      label: (
        <Link data-testid={`${DTI}__Item`} data-testvalue="calendar" to={routes.calendar}>
          {t('header.menuItems.calendar')}
        </Link>
      ),
    });

    if (isDesktop) {
      list.push({
        key: 'sep004',
        className: styles.separator,
        label: <HeaderMenuSeparator />,
      });
      list.push({ key: 'messenger', className: styles.messengerBtn, label: <MessengerHeaderButton /> });
    }

    list.push({
      key: ProfileSubmenuKey,
      icon: <UserIcon data-testid={`${DTI}__ProfileMenu`} />,
      label: !isDesktop ? (
        <span>
          {contact.firstName} {contact.lastName}
        </span>
      ) : null,
      popupClassName: styles.popup,
      children: ProfileMenu,
    });

    if (!isDesktop) {
      list.push({
        key: 'logout',
        icon: <IconOutlinedLogout />,
        label: (
          <AntButton
            data-testid={`${DTI}__LogOut`}
            type="link"
            onClick={() => void onLogout()}
            className={styles.logoutBtn}
            noPadding
          >
            {t('header.menuItems.logOut')}
          </AntButton>
        ),
      });
    }

    return list;
  }, [
    DTI,
    ProfileMenu,
    contact.firstName,
    contact.lastName,
    contact?.provider?.onboardingCompleted,
    isDesktop,
    onLogout,
    t,
  ]);

  const menuMode: AntMenuProps['mode'] = !isDesktop ? 'inline' : 'horizontal';

  return (
    <AntMenu
      data-testid={DTI}
      className={styles.mainMenu}
      theme="light"
      mode={menuMode}
      selectedKeys={selectedKeys}
      triggerSubMenuAction="click"
      items={MenuItems}
    />
  );
};
